<script setup lang="ts">
import IconMaterialSymbolsSyncRounded from '~icons/material-symbols/sync-rounded'
import Icon from '@/shared/components/Icon.vue'
import Dialog from '@/shared/components/Dialog.vue'
import { onBeforeUnmount, ref, watch } from 'vue'

import Button from '@/shared/components/Button.vue'
import { useAppVersion } from '@/shared/composables/useAppVersion'

// Seconds before the page auto-reloads if the user doesn't click. Within the
// 15-20s window the issue calls for — long enough to react, short enough that
// an unattended client still picks up the new version promptly.
const AUTO_RELOAD_SECONDS = 20

// Purely presentational: it reads the shared useAppVersion state and renders the
// reload prompt. The version subscription itself is opened in main.ts after
// auth, so it doesn't depend on this component being mounted.
const { updateAvailable, saveAllAndCloseDialogAndReloadPage } = useAppVersion()

const secondsRemaining = ref(AUTO_RELOAD_SECONDS)
let countdownHandle: ReturnType<typeof setInterval> | null = null

const stopCountdown = (): void => {
  if (countdownHandle) {
    clearInterval(countdownHandle)
    countdownHandle = null
  }
}

watch(updateAvailable, (available) => {
  if (!available) {
    stopCountdown()
    return
  }
  secondsRemaining.value = AUTO_RELOAD_SECONDS
  stopCountdown()
  countdownHandle = setInterval(() => {
    secondsRemaining.value -= 1
    if (secondsRemaining.value <= 0) {
      stopCountdown()
      void saveAllAndCloseDialogAndReloadPage()
    }
  }, 1000)
})

onBeforeUnmount(stopCountdown)

const handleReloadNow = (): void => {
  stopCountdown()
  void saveAllAndCloseDialogAndReloadPage()
}
</script>

<template>
  <Dialog
    :model-value="updateAvailable"
    :dismissible="false"
    content-class="min-w-[28rem]"
  >
    <template #header>
      <div class="flex items-center gap-2">
        <Icon
          class="h-6 w-6"
          severity="primary"
          :icon="IconMaterialSymbolsSyncRounded"
        />
        <span class="font-semibold">A new version is available</span>
      </div>
    </template>

    <p class="mb-2">
      A new version of the application has been deployed. The page needs to reload to load the latest updates.
    </p>
    <p class="text-sm text-surface-500">
      Your work will be saved automatically before reloading. The page will reload on its own in
      {{ secondsRemaining }} second<span v-if="secondsRemaining !== 1">s</span>.
    </p>

    <template #footer>
      <Button
        label="Reload Now"
        @click="handleReloadNow"
      />
    </template>
  </Dialog>
</template>
